import { task, timeout } from 'ember-concurrency';
import { ref } from 'ember-ref-bucket';
import { isSmallScreen, SELECT_ALL_ITEMS } from 'volta/components/bv-resource-list';
import BaseCell from 'volta/components/cell/base-cell';
import LocalPreferencesService, { formatBvTableKey } from 'volta/services/local-preferences';
import { indexByFn } from 'volta/utils/array-utils';
import {
  addClass, closest, getScrollParent, height, offset, removeClass
} from 'volta/utils/dom-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { THueVariationString } from 'volta/utils/style-utils';

import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
// @ts-ignore
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import { IBvIconArgs } from '../bv-icon';

const BASE_ROW_HEIGHT = 32;
const CONDENSED_ROW_HEIGHT = 31;
const VIRTUAL_SCROLL_THRESHOLD = 51;

export type TFixed = 'left' | 'right';

/**
 * Represents a column in a BvTable component.
 */
export interface IBvTableColumn {
  /**
   * The name of the column (used in header and as key)
   */
  columnName: string;

  /**
   * Path to get the cell value from the row model
   */
  valuePath: string;

  /**
   * Optional property name (used in collection views)
   */
  property?: string;

  /**
   * Key used for sorting (sent to the server)
   */
  sortKey?: string;

  /**
   * Whether the column is resizable
   */
  isResizable?: boolean;
  isStatic?: boolean;

  /**
   * Column fixed with
   */
  width?: number;

  /**
   * Column minimum width
   */
  minWidth?: number;

  /**
   * Column maximum width
   */
  maxWidth?: number;

  /**
   * Cell component
   */
  cellComponent?: string | unknown;

  /**
   * Footer cell component
   */
  footerCellComponent?: string | unknown;

  /**
   * Whether the column is fixed to the left or right
   */
  isFixed?: TFixed;

  /**
   * Whether the column is re-orderable
   */
  isReorderable?: boolean;

  /**
   * Whether the column is sortable
   */
  isSortable?: boolean;

  /**
   * Whether the column is groupable
   */
  isGroupable?: boolean;

  /**
   * Cell text alignment
   */
  textAlign?: 'center' | 'left' | 'right';

  /**
   * Whether to hide cell value until mouse over
   */
  showOnHover?: boolean;
  /**
   * Cell class names
   */
  classNames?: string;

  /**
   * Cell header class names
   */
  headerClassNames?: string;
  /**
   * Header icon left
   */
  headerIconLeft?: IBvIconArgs;

  /**
   * Header icon right
   */
  headerIconRight?: IBvIconArgs;
  /**
   * Whether the column is a custom field
   */
  isCustomField?: boolean;

  /**
   * Whether the column is a disabled
   */
  disabled?: boolean;

  /**
   * Cell color (Used in collection views)
   */
  color?: THueVariationString;

  /**
   * Format function fo cell value
   * @param model Model
   * @param row Row
   * @returns Formatted value
   */
  format?: (model: any, row: IBvTableRow) => any;

  /**
   * Footer format function fo cell value
   * @param model Model
   * @param row Row
   * @returns Formatted value
   */
  footerFormat?: (model: any, row: IBvTableRow) => any;
}

interface IPersistedColumn {
  valuePath: string;
  width?: number;
  isFixed?: TFixed;
}

interface IArgs {
  id: string;
  columns: IBvTableColumn[];
  selection: any[] | 'All';
  cellComponent?: string;
  footerComponent?: string;
  condensed?: boolean;
  rowHeight?: number;
  rows: object[];
  renderAll?: boolean;
  infiniteScroll?: boolean;
  loading?: boolean;
  persistColumnsConfig?: boolean;
  promotedBulkActions?: any[];
  bulkActions?: any[];
  widthConstraint?: 'eq-container' | 'gte-container' | 'lte-container' | 'none';
  fill?: boolean;
  containerSelector?: string;
  onRowClick?: (event: MouseEvent, rowMeta: IEmberTableRowMeta, rowValue: any) => void;
  onRowDoubleClick?: (event: MouseEvent, rowMeta: IEmberTableRowMeta, rowValue: any) => void;
  onSelect?: (rows: any[]) => void;
  onReorder?: (col1: IBvTableColumn, col2: IBvTableColumn, cols: IBvTableColumn[]) => void;
  onResize?: (column: IBvTableColumn) => void;
  onUpdateSorts?: (sorts: IBvTableSort[]) => void;
}

export interface IBvTableSort {
  isAscending: boolean;
  valuePath: string;
  sortKey?: string;
}

export interface IEmberTableRowMeta {
  isSelected: boolean;
  isCollapsed: boolean;
  index: number;
}

export interface IEmberTableRowApi {
  event: MouseEvent;
  rowValue?: any;
  rowMeta: IEmberTableRowMeta;
  cells: any[];
  rowSelectionMode: 'multiple' | 'none' | 'single';
}

export interface IBvTableRow {
  api: IEmberTableRowApi;
  columnValue: IBvTableColumn;
  rowValue?: any;
  cellValue?: any;
}

export default class BvTable extends Component<IArgs> {
  // Static utils

  static extractPersistedInfoFromColumn(column: IBvTableColumn | IPersistedColumn) {
    const { valuePath, width, isFixed } = column;
    return { valuePath, width, isFixed };
  }

  // Services

  @service localPreferences!: LocalPreferencesService;

  // Tracked Props

  @tracked columnsCache: IPersistedColumn[] = [];
  @tracked scrollContainer!: Element;
  @tracked containerSelector?: string;

  // Other props

  @ref('thead') thead!: HTMLElement;
  tbodyScrollContainer?: HTMLElement;
  headerScrollContainer: HTMLElement | null = null;

  // Getters

  get cellComponent() {
    return this.args.cellComponent ? ensureSafeComponent(this.args.cellComponent, this) : BaseCell;
  }

  get footerComponent() {
    return this.args.footerComponent
      ? ensureSafeComponent(this.args.footerComponent, this)
      : BaseCell;
  }

  get widthConstraint() {
    return this.args.widthConstraint ?? 'gte-container';
  }

  get rowHeight() {
    const { condensed, rowHeight } = this.args;
    return rowHeight ?? (condensed ? CONDENSED_ROW_HEIGHT : BASE_ROW_HEIGHT);
  }

  get renderAll() {
    const { rows, renderAll } = this.args;
    return renderAll ?? (rows && rows.length <= VIRTUAL_SCROLL_THRESHOLD);
  }

  get showInfinityLoader() {
    const { infiniteScroll, rows, loading } = this.args;
    return infiniteScroll && rows && rows.length && !loading;
  }

  get persistColumnsConfig() {
    return this.args.persistColumnsConfig ?? true;
  }

  @cached
  get validColumns() {
    const { columns = [] } = this.args;
    return columns.filter((c) => c);
  }

  @cached
  get columns() {
    const { columnsCache = [] } = this;

    if (!columnsCache.length) {
      return this.validColumns;
    }

    const indexedValidColumns = indexByFn(this.validColumns, (c) => c.valuePath);

    return columnsCache.map((col) => {
      const conf = indexedValidColumns[col.valuePath];
      return { ...(conf as IBvTableColumn), ...col, isFixed: col.isFixed };
    });
  }

  get canPersistConfig() {
    return Boolean(this.args.id && this.persistColumnsConfig);
  }

  get hasActions() {
    const { promotedBulkActions = [], bulkActions = [] } = this.args;
    return promotedBulkActions.concat(bulkActions).filter(isPresent).length > 0;
  }

  // Lifecycle hooks

  willDestroy() {
    this.tbodyScrollContainer?.removeEventListener('scroll', this.scrollEventHandler);
  }

  // Actions

  handleReorderColumns = (column1: IBvTableColumn, column2: IBvTableColumn) => {
    if (column1.isFixed !== column2.isFixed || column1.valuePath === column2.valuePath) {
      return;
    }
    const { columnsCache } = this;

    const firstElIndex = columnsCache.findIndex((c) => c.valuePath === column1.valuePath);
    const secondElIndex = columnsCache.findIndex((c) => c.valuePath === column2.valuePath);
    const col1 = columnsCache[firstElIndex];

    if (firstElIndex > -1 && secondElIndex > -1) {
      this.columnsCache = columnsCache.reduce((acc: IPersistedColumn[], column, index) => {
        if (index === firstElIndex) {
          return acc;
        }
        if (index === secondElIndex) {
          acc.push(col1);
        }
        acc.push(column);
        return acc;
      }, []);

      this.persistColumns();
    }

    this.args.onReorder?.(column1, column2, this.columns);
  };

  handleResizeColumns = (column: IBvTableColumn) => {
    this.setColumnWidth(column);
    this.args.onResize?.(column);
  };

  handleTableResize = () => {
    this.initCache();
    this.columns.map((c) => {
      this.setColumnWidth(c);
    });
  };

  onTableSort = (sorts: IBvTableSort[]) => {
    const { columns, onUpdateSorts } = this.args;
    const cols = columns || [];
    const updatedSorts = sorts.map((s) => {
      const col = cols.find((item) => {
        return item.valuePath === s.valuePath;
      });
      s.sortKey = col?.sortKey ?? s.valuePath;
      return s;
    });

    onUpdateSorts?.(updatedSorts);
  };

  onRowClick = ({ event, rowMeta, rowValue }: IEmberTableRowApi) => {
    const inputParent = closest(event.target as HTMLElement, 'input, button, label, a, select');
    if (!inputParent) {
      this.args.onRowClick?.(event, rowMeta, rowValue);
    }
  };

  onRowDoubleClick = ({ event, rowMeta, rowValue }: IEmberTableRowApi) => {
    const inputParent = closest(event.target as HTMLElement, 'input, button, label, a, select');
    if (!inputParent) {
      this.args.onRowDoubleClick?.(event, rowMeta, rowValue);
    }
  };

  handleToggleSelectAll = () => {
    const { rows = [], onSelect, selection } = this.args;
    const rowsArray = rows && typeof rows.toArray === 'function' ? rows.toArray() : rows.slice();
    let newlySelectedRows: any[] = [];

    if (
      (Array.isArray(selection) && selection.length === rowsArray.length) ||
      selection === SELECT_ALL_ITEMS
    ) {
      newlySelectedRows = [];
    } else {
      newlySelectedRows = rowsArray;
    }

    if (newlySelectedRows.length === 0 && !isSmallScreen()) {
      this.handleSelectMode(false);
    } else if (newlySelectedRows.length > 0) {
      this.handleSelectMode(true);
    }

    onSelect?.(newlySelectedRows);
  };

  handleSelectMode = (selectMode: boolean) => {
    if (!selectMode) {
      this.args.onSelect?.([]);
    }
  };

  /**
   * When the body is ready, we setup the scroll sync event handler
   *
   * @param tbody `table.bv-table tbody` element
   */
  handleTBodyInsert = (tbody: HTMLElement) => {
    const { thead } = this;
    this.headerScrollContainer = thead ? closest(thead, '.ember-table-overflow') : null;
    this.tbodyScrollContainer = closest(tbody, '.ember-table-overflow') as HTMLElement;

    if (this.headerScrollContainer && this.tbodyScrollContainer && !this.args.fill) {
      this.tbodyScrollContainer.addEventListener('scroll', this.scrollEventHandler);
    }
  };

  /**
   * When the .bv-table-header element is inserted into the DOM,
   * we initialise the container selector by trying to find the nearest scroll parent element
   * The container selector will be used by vertical-collection to calculate the visibility of items
   * in relation to the visible scrolling view.
   *
   * @param bvTableHeader `.bv-table-header` element
   */
  handleTableHeaderInsert = (bvTableHeader: HTMLElement) => {
    this.initCache();
    this.scrollContainer =
      getScrollParent(bvTableHeader)! ?? document.querySelector('.bv-app__content');
    this.containerSelector =
      this.args.fill || !this.scrollContainer
        ? undefined
        : '.' + this.scrollContainer?.className.split(' ').join('.');
  };

  scrollToRow = (rowModelId: string) => {
    if (!rowModelId || rowModelId === '') {
      return this.scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
    const row = this.tbodyScrollContainer?.querySelector(
      `#bv-table-row-${rowModelId}`
    ) as HTMLElement;

    if (row) {
      this.scrollToRowTask.perform(row);
    }
  };

  initCache = () => {
    this.columnsCache =
      this.getPersistedColumns() ??
      this.validColumns.map((c) => BvTable.extractPersistedInfoFromColumn(c));
  };

  /**
   * This function updates the columns cache with the current columns
   * It's mostly used when columns are resized or reordered or updated outside the component
   * We don't use the same logic as the initCache function because so we keep the existing width info of existing columns
   */
  updateCache = () => {
    if (!this.columnsCache || !this.columnsCache?.length) {
      this.initCache();
    } else {
      const cols = indexByFn(this.columnsCache, (c) => c.valuePath);
      this.columnsCache = this.validColumns.map((c) => {
        const col = cols[c.valuePath];
        if (col) {
          return col;
        }
        return BvTable.extractPersistedInfoFromColumn(c);
      });
    }
  };

  // Tasks

  scrollToRowTask = task(async (anchor: HTMLElement) => {
    try {
      const row = closest(anchor, 'tr') as HTMLElement;
      const rowOffset = offset(row).top;
      const rowHeight = height(row);
      const containerHeight = height(this.scrollContainer as HTMLElement);
      const top = this.scrollContainer.scrollTop - (containerHeight - rowHeight) / 2 + rowOffset;

      this.scrollContainer.scrollTo({ top, behavior: 'smooth' });
      if (row) {
        row && addClass(row, 'et-row-highlighted');
        await timeout(2100);
        removeClass(row, 'et-row-highlighted');
      }
    } catch (error) {
      defaultErrorHandler(error);
    }
  });

  // Private functions

  private persistColumns() {
    if (this.canPersistConfig) {
      this.localPreferences.setItem(
        formatBvTableKey(this.args.id),
        this.columnsCache.map((cache) => {
          const { width, ...toSave } = cache;
          return toSave;
        })
      );
    }
  }

  private getPersistedColumns(): IPersistedColumn[] | undefined {
    if (this.canPersistConfig) {
      const lsItem = this.localPreferences.getItem<IPersistedColumn[]>(
        formatBvTableKey(this.args.id)
      );

      if (lsItem) {
        const indexedValidColumns = indexByFn(this.validColumns, (c) => c.valuePath);
        return lsItem?.filter((c) => Boolean(indexedValidColumns[c.valuePath]));
      }
    }

    return undefined;
  }

  private setColumnWidth(column: IBvTableColumn) {
    if (column) {
      this.columnsCache = this.columnsCache.map((c) => {
        return BvTable.extractPersistedInfoFromColumn(
          c.valuePath === column.valuePath ? column : c
        );
      });
    }
  }

  /**
   * This function syncs the horizontal scroll of the table sticky header
   * with the body horizontal scroll
   */
  private scrollEventHandler = () => {
    const { headerScrollContainer, tbodyScrollContainer } = this;
    if (headerScrollContainer && tbodyScrollContainer) {
      headerScrollContainer.scrollLeft = tbodyScrollContainer.scrollLeft;
    }
  };
}
